import { config } from "../config";

const DEFAULT_AGENT_URL = "https://cdn.seondf.com/js/v6/agent.js";
const DEFAULT_TIMEOUT_MS = 4000;
const MOCK_STORAGE_KEY = "raas_seon_mock_session";

type SeonAgent = {
  init?: (options?: Record<string, unknown>) => void | Promise<void>;
  getSession: (
    options?: Record<string, unknown>,
  ) => Promise<string> | string;
};

declare global {
  interface Window {
    seon?: SeonAgent;
  }
}

function isEnabled(): boolean {
  const flag = config.SEON_DEVICE_ENABLED;
  return flag === true || flag === "true";
}

function isMock(): boolean {
  const flag = config.SEON_DEVICE_MOCK;
  return flag === true || flag === "true";
}

function timeoutMs(): number {
  const raw = Number(config.SEON_DEVICE_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_TIMEOUT_MS;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error("SEON getSession timeout")),
      ms,
    );
    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        window.clearTimeout(timer);
        reject(err);
      });
  });
}

function loadAgentScript(src: string): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window"));
  }
  if (window.seon?.getSession) {
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-seon-agent="true"]',
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("SEON agent script failed")),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.seonAgent = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("SEON agent script failed"));
    document.head.appendChild(script);
  });
}

function getOrCreateMockSession(): string {
  try {
    const existing = sessionStorage.getItem(MOCK_STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
  } catch {
    /* private mode */
  }
  const mock = `mock_seon_${crypto.randomUUID().replace(/-/g, "")}`;
  try {
    sessionStorage.setItem(MOCK_STORAGE_KEY, mock);
  } catch {
    /* ignore */
  }
  return mock;
}

/**
 * Collect an encrypted SEON Device Intelligence session for the backend.
 * Returns `null` when disabled, timed out, or the agent is unavailable —
 * callers must continue without blocking the UX.
 */
export async function getDeviceSession(): Promise<string | null> {
  if (!isEnabled()) return null;

  if (isMock()) {
    return getOrCreateMockSession();
  }

  try {
    const agentUrl = config.SEON_AGENT_URL?.trim() || DEFAULT_AGENT_URL;
    await withTimeout(loadAgentScript(agentUrl), timeoutMs());

    const agent = window.seon;
    if (!agent?.getSession) return null;

    try {
      await agent.init?.({});
    } catch {
      /* init is optional / may already be done */
    }

    const session = await withTimeout(
      Promise.resolve(agent.getSession({})),
      timeoutMs(),
    );
    const trimmed = typeof session === "string" ? session.trim() : "";
    return trimmed.length >= 8 ? trimmed : null;
  } catch {
    return null;
  }
}
